"use client";

import React, { useState } from "react";
import { Plus } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
const faqData = [{
  question: "How are mechanical bulls shipped?",
  answer: "Mechanical bulls generally require planned equipment delivery rather than ordinary parcel shipping. The exact arrangement depends on the machine, package, packed size, weight, destination and receiving requirements."
}, {
  question: "How much does it cost to ship a mechanical bull?",
  answer: "Shipping cost varies according to the exact equipment, packed dimensions, weight, destination and delivery requirements. Ask for order-specific shipping information rather than relying on one generic freight price."
}, {
  question: "Does a mechanical bull ship with the inflatable?",
  answer: "It depends on the product package. Some listings may include an inflatable safety arena and blower, while others may not. Check the individual listing before ordering."
}, {
  question: "Do I need special equipment to receive a mechanical bull?",
  answer: "Receiving requirements depend on the actual shipment and delivery arrangement. Confirm unloading and access needs before dispatch."
}, {
  question: "Can mechanical bulls be delivered to a residence?",
  answer: "Delivery options depend on the machine, destination and available shipping arrangement. Provide the destination details during the enquiry so the options for that specific order can be reviewed."
}, {
  question: "How do I order a mechanical bull?",
  answer: "Choose a machine from the current inventory, send an enquiry using the product name or SKU, confirm the product and delivery information, and then follow the order instructions provided for that purchase."
}, {
  question: "Do mechanical bulls come with a warranty?",
  answer: "Warranty terms vary by product, manufacturer, condition and seller. Review the warranty information for the exact machine and ask if anything is unclear before purchasing."
}, {
  question: "Can I order replacement parts after buying a bull?",
  answer: "Available parts can be purchased separately when suitable components are in stock. Confirm compatibility with the exact machine before ordering replacement parts."
}];
export default function ShippingFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const toggleItem = (index: number) => {
    setOpenIndex(prev => prev === index ? null : index);
  };
  return <section id="shippingorderingfaq" data-nav="light" className="w-full bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 border-t border-border">
      <div className="max-w-4xl mx-auto">
        {/* Header Block */}
        <div className="mb-12 text-left sm:text-center">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-muted border border-border text-muted-foreground font-mono text-xs uppercase tracking-wider mb-4">
            <span>Logistics &amp; Procurement Protocol</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground uppercase">
            Mechanical Bull Shipping &amp; Ordering FAQ
          </h2>
          <p className="mt-3 text-muted-foreground text-sm sm:text-base max-w-2xl sm:mx-auto">
            Review critical freight arrangements, delivery conditions, and order guidelines before submitting your equipment inquiry.
          </p>
        </div>

        {/* Bounded Accordion List Container */}
        <div className="border-t border-border">
          {faqData.map((item, index) => {
          const isOpen = openIndex === index;
          const itemNumber = (index + 1).toString().padStart(2, "0");
          return <motion.div key={index} data-index={index} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 12
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            ease: [0.16, 1, 0.3, 1],
            delay: index * 0.04
          }} className="border-b border-border">
                <Button type="button" variant="ghost" onClick={() => toggleItem(index)} aria-expanded={isOpen} className="w-full h-auto py-5 px-3 sm:px-4 flex items-center justify-between text-left hover:bg-muted/40 rounded-none group transition-colors focus-visible:ring-1 focus-visible:ring-primary">
                  <div className="flex items-start sm:items-center gap-3 sm:gap-4 pr-4">
                    <span className="font-mono text-xs text-muted-foreground shrink-0 mt-1 sm:mt-0">
                      {itemNumber}
                    </span>
                    <span className="font-serif text-lg sm:text-xl font-semibold uppercase tracking-wide text-foreground group-hover:text-primary transition-colors text-left">
                      {item.question}
                    </span>
                  </div>

                  <div className="shrink-0 flex items-center justify-center size-8 bg-muted/60 border border-border text-foreground transition-colors group-hover:border-primary/50 group-hover:text-primary">
                    <motion.div animate={{
                  rotate: isOpen ? 45 : 0
                }} transition={{
                  duration: 0.18,
                  ease: "easeOut"
                }} className="flex items-center justify-center">
                      <Plus className="size-4" />
                    </motion.div>
                  </div>
                </Button>

                <AnimatePresence initial={false}>
                  {isOpen && <motion.div key="content" initial={shouldReduceMotion ? {
                opacity: 0
              } : {
                height: 0,
                opacity: 0
              }} animate={shouldReduceMotion ? {
                opacity: 1
              } : {
                height: "auto",
                opacity: 1
              }} exit={shouldReduceMotion ? {
                opacity: 0
              } : {
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.22,
                ease: [0.2, 0, 0, 1]
              }} className="overflow-hidden">
                      <div className="px-3 sm:px-4 pb-6 pt-1 text-sm sm:text-base text-muted-foreground leading-relaxed pl-10 sm:pl-11">
                        {item.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}